import { ChartPieIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { trackEvent } from '@/utils/analytics';

interface SearchViewSegmentedControlProps {
  selectedView: 'tables' | 'graphs';
  onViewChange: (view: 'tables' | 'graphs') => void;
  className?: string;
  variant?: 'full' | 'collapsed';
  analyticsSection?: string;
  analyticsButtonId?: string;
}

const VIEW_OPTIONS = [
  { id: 'tables' as const, label: 'Tables view', Icon: TableCellsIcon },
  { id: 'graphs' as const, label: 'Graphs view', Icon: ChartPieIcon },
];

export function SearchViewSegmentedControl({
  selectedView,
  onViewChange,
  className,
  variant = 'full',
  analyticsSection = 'sidebar_controls',
  analyticsButtonId = 'sidebar_switch_view',
}: SearchViewSegmentedControlProps) {
  const handleSelect = (view: 'tables' | 'graphs') => {
    if (view === selectedView) return;
    trackEvent({
      name: 'view_switch',
      buttonId: variant === 'collapsed' ? `${analyticsButtonId}_collapsed` : analyticsButtonId,
      label: `Switch to ${view} view`,
      page: 'search_results',
      section: analyticsSection,
      feature: 'view',
      eventGroup: 'search',
      eventCategory: 'search',
    });
    onViewChange(view);
  };

  if (variant === 'collapsed') {
    return (
      <div
        role="group"
        aria-label="Search results view"
        className={clsx('flex flex-col items-center gap-1', className)}
        data-testid="search-view-segmented-control-collapsed"
      >
        {VIEW_OPTIONS.map(({ id, label, Icon }) => {
          const isActive = selectedView === id;
          return (
            <BaseTooltip key={id} content={label} position="left">
              <button
                type="button"
                aria-label={label}
                aria-pressed={isActive}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D98ABA]"
                style={{
                  color: isActive ? '#be0080' : '#6b7180',
                  backgroundColor: isActive ? 'rgba(236, 42, 153, 0.08)' : 'transparent',
                }}
                onClick={() => handleSelect(id)}
              >
                <Icon className="h-5 w-5" />
              </button>
            </BaseTooltip>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      aria-label="Search results view"
      className={clsx(
        'inline-flex h-8 w-full items-stretch rounded-3xl border border-slate-200 bg-slate-100/90 p-0.5 shadow-sm',
        className,
      )}
      data-testid="search-view-segmented-control"
    >
      {VIEW_OPTIONS.map(({ id, label, Icon }) => {
        const isActive = selectedView === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={label}
            className={`inline-flex flex-1 items-center justify-center gap-1 rounded-3xl px-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-1 ${
              isActive ? 'bg-white text-slate-900 shadow-lg' : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => handleSelect(id)}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{id === 'tables' ? 'Tables' : 'Graphs'}</span>
          </button>
        );
      })}
    </div>
  );
}
